<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} Top Talkers Job</h1>
    </div>

    <!-- Page-level Error Banner -->
    <div v-if="pageError" class="bg-red-50 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4 flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-medium">{{ pageError }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <JobDescriptionInput v-model="formData.job_description" :error="fieldErrors.job_description" :job-name="jobName" />

      <!-- Interface (Dropdown) -->
      <div class="flex flex-col">
        <label for="interface" class="mb-1 font-medium">
          Network Interface<span class="text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="formData.interface"
          :options="interfaceOptions"
          :searchable="true"
          valueKey="value"
          labelKey="label"
          :placeholder="loadingInterfaces ? 'Loading interfaces...' : 'Select an interface'"
          :disabled="loadingInterfaces"
          :error="!!fieldErrors.interface"
        />
        <p class="text-sm text-gray-500 mt-1">
          The network interface to monitor for traffic (SPAN port)
          <span v-if="interfaceError" class="text-red-500 ml-2">{{ interfaceError }}</span>
        </p>

        <!-- Display badge when SPAN-ready interface is selected -->
        <div v-if="formData.interface && isInterfacePromisc(formData.interface)"
             class="mt-2">
          <span class="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            Ready For SPAN Monitoring
          </span>
        </div>

        <FieldError :message="fieldErrors.interface" />
      </div>

      <!-- Local Network (SPAN Monitoring) -->
      <div class="flex flex-col">
        <label for="local_network" class="mb-1 font-medium">
          Local Network (SPAN Monitoring)<span class="text-red-500">*</span>
        </label>
        <input
          id="local_network"
          v-model="formData.local_network"
          type="text"
          required
          placeholder="e.g., 10.10.10.0/24"
          :class="inputClass('local_network')"
        />
        <p class="text-sm text-gray-500 mt-1">
          The network range to monitor via the SPAN port. All traffic entering or leaving this
          network will be tracked and reported. Use CIDR notation (e.g., 10.10.10.0/24 for a
          256-host subnet, or 10.10.0.0/16 for a larger network).
        </p>
        <FieldError :message="fieldErrors.local_network" />
      </div>

      <!-- Berkeley Packet Filter Builder -->
      <div class="flex flex-col space-y-2">
        <label class="mb-1 font-medium">
          Berkeley Packet Filter <span class="text-gray-500 text-sm">(optional)</span>
        </label>

        <!-- Filter Builder Controls -->
        <div class="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
          <!-- Default Operator Selector -->
          <div class="mb-3 flex items-center space-x-2">
            <label class="text-xs text-gray-700 dark:text-gray-300">
              Default operator for new filters:
            </label>
            <GlassSelect
              v-model="bpfDefaultOperator"
              :options="[{value:'or',label:'OR'},{value:'and',label:'AND'}]"
              valueKey="value"
              labelKey="label"
              placeholder="Operator"
              class="!w-auto"
            />
            <span class="text-xs text-gray-500 dark:text-gray-400">
              (you can change per-filter below)
            </span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <!-- Filter Type -->
            <div class="flex flex-col">
              <label for="filter_type" class="text-xs mb-1 text-gray-700 dark:text-gray-300">Filter Type</label>
              <GlassSelect
                v-model="filterBuilder.type"
                :options="filterTypeOptions"
                valueKey="value"
                labelKey="label"
                placeholder="Select type..."
              />
            </div>

            <!-- Direction (only for host/net) -->
            <div class="flex flex-col" v-if="filterBuilder.type === 'host' || filterBuilder.type === 'net'">
              <label for="filter_direction" class="text-xs mb-1 text-gray-700 dark:text-gray-300">Direction</label>
              <GlassSelect
                v-model="filterBuilder.direction"
                :options="filterDirectionOptions"
                valueKey="value"
                labelKey="label"
                placeholder="Both (default)"
              />
            </div>

            <!-- Value Input -->
            <div class="flex flex-col" :class="filterBuilder.type === 'port' ? 'md:col-span-2' : ''">
              <label for="filter_value" class="text-xs mb-1 text-gray-700 dark:text-gray-300">{{ filterValueLabel }}</label>
              <input
                id="filter_value"
                v-model="filterBuilder.value"
                type="text"
                :placeholder="filterValuePlaceholder"
                class="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                @keyup.enter="addFilterRule"
              />
            </div>

            <!-- Add Button -->
            <div class="flex items-end">
              <button
                type="button"
                @click="addFilterRule"
                :disabled="!canAddFilter"
                :class="[
                  'w-full px-3 py-1.5 rounded text-sm font-medium transition-colors',
                  canAddFilter
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                ]"
              >
                Add Filter
              </button>
            </div>
          </div>

          <!-- Active Filters Display -->
          <div v-if="bpfFilters.length > 0" class="space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300">Active Filters:</span>
              <button
                type="button"
                @click="clearAllFilters"
                class="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
              >
                Clear All
              </button>
            </div>

            <div class="flex flex-wrap gap-2 items-center">
              <template v-for="(filter, index) in bpfFilters" :key="index">
                <!-- Operator selector (for filters after the first) -->
                <GlassSelect
                  v-if="index > 0"
                  v-model="filter.operator"
                  :options="[{value:'or',label:'OR'},{value:'and',label:'AND'}]"
                  valueKey="value"
                  labelKey="label"
                  placeholder="Operator"
                  class="!w-20"
                />

                <!-- Filter badge -->
                <span
                  class="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-sm flex items-center space-x-2"
                >
                  <span>{{ typeof filter === 'string' ? filter : filter.value }}</span>
                  <button
                    type="button"
                    @click="removeFilter(index)"
                    class="hover:text-blue-600 dark:hover:text-blue-300 font-bold"
                  >
                    ×
                  </button>
                </span>
              </template>
            </div>

            <!-- Generated Filter Syntax Display -->
            <div class="mt-3 p-2 bg-gray-200 dark:bg-gray-800 rounded">
              <div class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Generated Berkeley Packet Filter:</div>
              <code class="text-xs text-gray-800 dark:text-gray-200 font-mono">{{ generatedBpfFilter }}</code>
            </div>
          </div>
        </div>

        <FieldError :message="fieldErrors.bpf_filter" />
        <p class="text-sm text-amber-600 dark:text-amber-400 mt-2">
          <strong>Note:</strong> Packet filters may not work correctly with VLAN-tagged (802.1Q) traffic
          from SPAN ports. If you're monitoring tagged traffic, consider leaving this field empty
          and relying on the Local Network setting above to define monitoring scope.
        </p>
      </div>

      <!-- Scrape Settings -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label for="scrape_interval" class="mb-1 font-medium">Scrape Interval (seconds)</label>
          <input
            id="scrape_interval"
            v-model.number="formData.scrape_interval"
            type="number"
            min="10"
            class="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="flex flex-col">
          <label for="scrape_timeout" class="mb-1 font-medium">Scrape Timeout (seconds)</label>
          <input
            id="scrape_timeout"
            v-model.number="formData.scrape_timeout"
            type="number"
            min="5"
            class="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          @click="goBack"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {{ isEditMode ? 'Update' : 'Create' }} Top Talkers Job
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createAlloyDarkstat, getAlloyDarkstatById, updateAlloyDarkstat } from "@/services/alloyService";
import { getAlloyConfigById } from "@/services/alloyService";
import { getProbeNetworkInterfaces } from "@/services/probeService";
import FieldError from "@/components/FieldError.vue";
import JobDescriptionInput from "@/components/JobDescriptionInput.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddAlloyDarkstat",
  components: { FieldError, JobDescriptionInput, PageError, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      pageError: null,
      jobName: "",
      formData: {
        job_description: "",
        interface: "",
        local_network: "",
        scrape_interval: 60,
        scrape_timeout: 30,
      },
      // Berkeley Packet Filter Builder
      filterBuilder: {
        type: "",
        direction: "",
        value: "",
      },
      bpfFilters: [], // Array of filter objects: [{value: "host 1.2.3.4", operator: "or"}]
      bpfDefaultOperator: 'or', // Default operator for new filters
      canAdd: false,
      isLoading: false,
      entityName: "", // To display generated job name
      probeId: null, // Store probe ID
      availableInterfaces: [], // Network interfaces from probe
      interfaceDetails: {}, // Store full details with promisc status
      promiscInterfaces: [], // List of promisc interface names
      loadingInterfaces: false,
      interfaceError: null,
      filterTypeOptions: [
        { value: "host", label: "Host" },
        { value: "port", label: "Port" },
        { value: "net", label: "Network" },
        { value: "custom", label: "Custom Expression" },
      ],
      filterDirectionOptions: [
        { value: "src", label: "Source" },
        { value: "dst", label: "Destination" },
      ],
    };
  },
  computed: {
    currentSchema() { return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null; },
    interfaceOptions() {
      return this.availableInterfaces.map((iface) => ({
        value: iface,
        label: this.isInterfacePromisc(iface) ? `${iface} [SPAN READY]` : iface,
      }));
    },
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    configId() {
      return this.$route.params.configId;
    },
    jobId() {
      return this.$route.params.jobId;
    },
    isEditMode() {
      return !!this.jobId;
    },

    // Generated job name display
    generatedJobName() {
      if (this.entityName) {
        return `Alloy-Darkstat-${this.entityName}`;
      }
      return "Will be auto-generated";
    },

    // Berkeley Packet Filter computed properties
    filterValueLabel() {
      const labels = {
        host: "IP Address",
        port: "Port Number",
        net: "Network (CIDR)",
        custom: "Filter Expression"
      };
      return labels[this.filterBuilder.type] || "Value";
    },

    filterValuePlaceholder() {
      const placeholders = {
        host: "e.g., 192.168.1.1",
        port: "e.g., 80 or 443",
        net: "e.g., 10.0.0.0/8",
        custom: "e.g., tcp and port 443"
      };
      return placeholders[this.filterBuilder.type] || "Enter value";
    },

    canAddFilter() {
      return this.filterBuilder.type && this.filterBuilder.value.trim();
    },

    generatedBpfFilter() {
      if (this.bpfFilters.length === 0) return "";
      if (this.bpfFilters.length === 1) {
        // Handle both old string format and new object format
        const filter = this.bpfFilters[0];
        return typeof filter === 'string' ? filter : filter.value;
      }

      // Build filter string with operators
      let result = "";

      for (let i = 0; i < this.bpfFilters.length; i++) {
        const filter = this.bpfFilters[i];

        // Handle backwards compatibility: old string format vs new object format
        const filterValue = typeof filter === 'string' ? filter : filter.value;
        const operator = typeof filter === 'string' ? 'or' : (filter.operator || 'or');

        if (i === 0) {
          result = filterValue;
        } else {
          result += ` ${operator} ${filterValue}`;
        }
      }

      return result;
    },

    // Check if an interface is in promiscuous mode
    isInterfacePromisc() {
      return (interfaceName) => {
        return this.promiscInterfaces.includes(interfaceName);
      };
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    await this.fetchAlloyConfig(); // Get probe ID and entity name
    if (this.probeId) {
      await this.fetchNetworkInterfaces(); // Get interfaces from probe
    }
    if (this.isEditMode) {
      await this.loadExistingJob();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add Alloy Darkstat Job");
      }
    },
    async loadExistingJob() {
      this.isLoading = true;
      try {
        const job = await getAlloyDarkstatById(this.jobId, this.currentSchema);
        this.jobName = job.job_name;
        this.formData.job_description = job.job_description || "";
        this.formData.interface = job.interface;
        this.formData.local_network = job.local_network || "";
        this.formData.scrape_interval = job.scrape_interval || 60;
        this.formData.scrape_timeout = job.scrape_timeout || 30;

        // Load Berkeley Packet Filter and parse into filter array
        if (job.bpf_filter) {
          // Parse the filter string - could be with 'or' or 'and' operators
          // For now, split by common operators and convert to object format
          const filterStrings = job.bpf_filter.split(/\s+(or|and)\s+/i);
          const filters = [];

          for (let i = 0; i < filterStrings.length; i++) {
            const part = filterStrings[i].trim();

            // Skip operator keywords themselves
            if (part.toLowerCase() === 'or' || part.toLowerCase() === 'and') {
              continue;
            }

            // Determine operator: check what came before this filter
            let operator = null;
            if (i > 1) {
              const prevPart = filterStrings[i - 1].toLowerCase();
              operator = (prevPart === 'or' || prevPart === 'and') ? prevPart : 'or';
            }

            filters.push({
              value: part,
              operator: operator
            });
          }

          this.bpfFilters = filters;
        }
      } catch (error) {
        console.error("Error loading Darkstat job:", error);
        alert("Failed to load job data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    async fetchAlloyConfig() {
      try {
        // Fetch the AlloyConfig to get probe ID and entity name
        const config = await getAlloyConfigById(this.configId, this.currentSchema);
        this.entityName = config.linked_entity_name || config.probe_name || "Unknown";
        this.probeId = config.probe; // Get probe ID
      } catch (error) {
        console.error("Error fetching Alloy config:", error);
        this.entityName = "Unknown";
      }
    },
    async fetchNetworkInterfaces() {
      if (!this.probeId) {
        this.interfaceError = "No probe associated with this configuration";
        return;
      }

      this.loadingInterfaces = true;
      this.interfaceError = null;

      try {
        const response = await getProbeNetworkInterfaces(this.probeId);
        if (response.interfaces && response.interfaces.length > 0) {
          this.availableInterfaces = response.interfaces;
          this.interfaceDetails = response.details || {}; // Store enhanced details
          this.promiscInterfaces = response.promisc_interfaces || []; // Store promisc list
        } else if (response.error) {
          this.interfaceError = response.error;
          // Fallback to text input if can't get interfaces
          this.availableInterfaces = [];
          this.interfaceDetails = {};
          this.promiscInterfaces = [];
        }
      } catch (error) {
        console.error("Error fetching network interfaces:", error);
        this.interfaceError = "Could not retrieve interfaces from probe";
        this.availableInterfaces = [];
        this.interfaceDetails = {};
        this.promiscInterfaces = [];
      } finally {
        this.loadingInterfaces = false;
      }
    },

    // Berkeley Packet Filter Builder Methods
    addFilterRule() {
      if (!this.canAddFilter) return;

      let filterExpression = "";
      const { type, direction, value } = this.filterBuilder;

      switch (type) {
        case "host":
          filterExpression = direction
            ? `${direction} host ${value.trim()}`
            : `host ${value.trim()}`;
          break;

        case "port":
          filterExpression = `port ${value.trim()}`;
          break;

        case "net":
          filterExpression = direction
            ? `${direction} net ${value.trim()}`
            : `net ${value.trim()}`;
          break;

        case "custom":
          filterExpression = value.trim();
          break;
      }

      if (filterExpression) {
        // Check if filter already exists (by value)
        const exists = this.bpfFilters.some(f => f.value === filterExpression);
        if (!exists) {
          const isFirstFilter = this.bpfFilters.length === 0;
          this.bpfFilters.push({
            value: filterExpression,
            operator: isFirstFilter ? null : this.bpfDefaultOperator
          });
        }
      }

      // Reset builder
      this.filterBuilder.value = "";
    },

    removeFilter(index) {
      this.bpfFilters.splice(index, 1);
    },

    clearAllFilters() {
      this.bpfFilters = [];
    },

    inputClass(field) {
      return [
        'border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600',
        this.fieldErrors[field] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
      ];
    },
    goBack() {
      this.$router.push({ name: "AlloyDetails", params: { id: this.configId }, query: { schemaName: this.currentSchema, tab: "darkstat" } });
    },
    async submitForm() {
      this.pageError = null;
      this.fieldErrors = {};

      const data = {
        alloy_config: parseInt(this.configId),
        job_description: this.formData.job_description.trim(),
        // job_name will be auto-generated on backend
        // org_id is set automatically from organization's Mimir tenant setting
        interface: this.formData.interface,
        local_network: this.formData.local_network,
        scrape_interval: this.formData.scrape_interval,
        scrape_timeout: this.formData.scrape_timeout,
      };

      // Add Berkeley Packet Filter if any filters exist
      if (this.bpfFilters.length > 0) {
        data.bpf_filter = this.generatedBpfFilter;
      }

      try {
        if (this.isEditMode) {
          await updateAlloyDarkstat(this.jobId, data, this.currentSchema);
        } else {
          await createAlloyDarkstat(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        this.pageError = null;
        this.fieldErrors = {};

        const errData = error?.response?.data;
        if (errData) {
          // Handle field-specific errors
          Object.keys(errData).forEach((key) => {
            const errorMsg = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
            this.fieldErrors[key] = errorMsg;
          });

          // Show page-level error for non-field errors or alloy_config errors
          // Replace technical backend error messages with user-friendly versions
          if (errData.alloy_config || errData.non_field_errors) {
            let errorMessage = errData.alloy_config?.[0] || errData.alloy_config ||
                               errData.non_field_errors?.[0] || errData.non_field_errors;

            // Replace technical error with client-friendly message
            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('mimir')) {
              errorMessage = "Your organization tenant is not configured. Please contact Splitpoint support for assistance.";
            }

            this.pageError = errorMessage || "Please correct the errors below";
          } else if (Object.keys(errData).length > 0) {
            this.pageError = "Please correct the errors below";
          }
        } else {
          // Generic error message for network/server errors
          this.pageError = `Failed to ${this.isEditMode ? 'update' : 'create'} job. Please try again.`;
        }

        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} Darkstat job:`, error);
      }
    },
  },
};
</script>
